// components/data-quality/QualityDistributionChart.jsx
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartPie, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const QualityDistributionChart = ({ distribution, totalBookings }) => {
  const chartData = {
    labels: [
      "Excellent (90-100%)",
      "Good (70-89%)",
      "Average (50-69%)",
      "Poor (30-49%)",
      "Critical (<30%)",
    ],
    datasets: [
      {
        data: [
          distribution.excellent,
          distribution.good,
          distribution.average,
          distribution.poor,
          distribution.critical,
        ],
        backgroundColor: [
          "rgba(34, 197, 94, 0.8)", // Green for excellent
          "rgba(59, 130, 246, 0.8)", // Blue for good
          "rgba(234, 179, 8, 0.8)", // Yellow for average
          "rgba(249, 115, 22, 0.8)", // Orange for poor
          "rgba(239, 68, 68, 0.8)", // Red for critical
        ],
        borderColor: [
          "rgba(34, 197, 94, 1)",
          "rgba(59, 130, 246, 1)",
          "rgba(234, 179, 8, 1)",
          "rgba(249, 115, 22, 1)",
          "rgba(239, 68, 68, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          boxWidth: 15,
          font: {
            size: 11,
          },
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return chart.data.labels.map((label, i) => {
              const value = datasets[0].data[i];
              const percentage = ((value / totalBookings) * 100).toFixed(1);
              return {
                text: `${label} (${value}, ${percentage}%)`,
                fillStyle: datasets[0].backgroundColor[i],
                strokeStyle: datasets[0].borderColor[i],
                lineWidth: 1,
                hidden: false,
                index: i,
              };
            });
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const percentage = ((value / totalBookings) * 100).toFixed(1);
            return `${context.label.split(" ")[0]}: ${value} (${percentage}%)`;
          },
        },
      },
      datalabels: {
        formatter: (value, ctx) => {
          if (value < totalBookings * 0.05) return ""; // Hide small slices
          return ((value / totalBookings) * 100).toFixed(0) + "%";
        },
        color: "#fff",
        font: {
          weight: "bold",
          size: 11,
        },
      },
    },
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faChartPie} className="text-blue-600 mr-2" />
          Data Quality Distribution
        </h3>
        <div className="text-sm text-gray-500 flex items-center">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
          <span>Total: {totalBookings} bookings</span>
        </div>
      </div>
      <div className="flex-grow">
        <Doughnut data={chartData} options={chartOptions} />
      </div>
      <div className="mt-4 text-sm text-center text-gray-500">
        <span className="font-medium">Quality Assessment Criteria: </span>
        Status progression, date sequencing, and completeness
      </div>
    </div>
  );
};

export default QualityDistributionChart;
